/**
 * Upload por URL assinada — Lambda **Gestão de Documentos** (contrato Compliance v4).
 *
 * O fluxo tem três pernas, e só a do meio não passa pelo `apiUser`:
 *
 *  1. `POST …/upload-url` (API User, com token) → `{uploadUrl, chave, cabecalhos}`;
 *  2. **`PUT uploadUrl` direto no S3**, com os `cabecalhos` devolvidos e **sem
 *     `Authorization`** — a assinatura está na própria URL, e mandar o Bearer do
 *     Cognito para o S3 vazaria o token para outro host e quebraria a assinatura;
 *  3. `POST` de registro (API User) com a `chave`.
 *
 * A Lambda nunca recebe bytes. Este módulo cuida das pernas 1 e 2; o registro é
 * de quem chama, porque cada agregado (evidência, manual) tem o seu corpo.
 */
import { ErroDeApi } from '@/api/clients/criarCliente'
import type { UrlDeUpload } from '@/api/contracts/compliance'

export type TipoDeArquivo = 'pdf' | 'docx'

const MIME: Record<TipoDeArquivo, string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
}

/** `pdf` / `docx` pelo MIME ou, na falta dele, pela extensão. `null` = não suportado. */
export function tipoDoArquivo(arquivo: { name: string; type: string }): TipoDeArquivo | null {
  if (arquivo.type === MIME.pdf) return 'pdf'
  if (arquivo.type === MIME.docx) return 'docx'
  const extensao = arquivo.name.toLowerCase().split('.').pop()
  return extensao === 'pdf' || extensao === 'docx' ? extensao : null
}

/** A perna 2: o PUT no S3. Deliberadamente `fetch` cru, sem o cliente da API. */
export async function enviarParaUrlAssinada(destino: UrlDeUpload, arquivo: Blob): Promise<void> {
  let resposta: Response
  try {
    resposta = await fetch(destino.uploadUrl, {
      method: 'PUT',
      headers: destino.cabecalhos,
      body: arquivo,
    })
  } catch {
    throw new ErroDeApi('Não foi possível enviar o arquivo ao armazenamento. Tente novamente.', 0)
  }
  if (!resposta.ok) {
    throw new ErroDeApi(
      `O armazenamento recusou o arquivo (HTTP ${resposta.status}). A URL assinada pode ter expirado — tente novamente.`,
      resposta.status,
    )
  }
}

/**
 * Pernas 1 e 2 juntas. Devolve a `chave` para o registro.
 *
 * `pedirUrl` recebe o que o contrato pede (`nomeArquivo`, `tipo`, `tamanhoBytes`)
 * e faz o POST ao endpoint certo — evidência ou manual.
 */
export async function enviarArquivo(
  arquivo: File,
  pedirUrl: (meta: {
    nomeArquivo: string
    tipo: TipoDeArquivo
    tamanhoBytes: number
  }) => Promise<UrlDeUpload>,
): Promise<{ chave: string; tipo: TipoDeArquivo; tamanhoBytes: number }> {
  const tipo = tipoDoArquivo(arquivo)
  if (!tipo) {
    throw new ErroDeApi(`${arquivo.name}: só PDF ou DOCX.`, 400, {
      codigo: 'tipo_nao_suportado',
      message: `${arquivo.name}: só PDF ou DOCX.`,
    })
  }
  const destino = await pedirUrl({ nomeArquivo: arquivo.name, tipo, tamanhoBytes: arquivo.size })
  await enviarParaUrlAssinada(destino, arquivo)
  return { chave: destino.chave, tipo, tamanhoBytes: arquivo.size }
}
